import React, { useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import NewPatient from '../Components/PatientCenter/NewPatient';
import PatientForms from '../Components/PatientCenter/PatientForms';
import RefillRequests from '../Components/PatientCenter/RefillRequests';
import OfficePolicies from '../Components/PatientCenter/OfficePolicies';
import FAQs from '../Components/PatientCenter/FAQs';
import InfoCard from '../Components/InfoCard';
import { useNavigateAndScroll } from '../Hooks/useNavigateAndScroll';

function PatientCenter() {
  const barRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const navigateAndScroll = useNavigateAndScroll();

  // Placeholder data for patient center info cards
  const infoCardsData = {
    newPatient: {
      image: 'https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/New+Pt+registration.webp',
      infoTitle: 'New Patient Registration',
      infoText: 'Quickly register online for your first visit. Fill out personal, insurance, and health history details to get started with our practice in just a few minutes.',
      path: '/patient-center/new-patient'
    },
    patientForms: {
      image: 'https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/Patient+forms.webp',
      infoTitle: 'Patient Forms',
      infoText: 'Access all necessary intake and consent forms to streamline your visit. Download, complete, and upload them securely before your appointment.',
      path: '/patient-center/patient-forms'
    },
    refillRequests: {
      image: 'https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/Med+refill.webp',
      infoTitle: 'Refill Requests',
      infoText: 'Submit medication refill requests online anytime. Our team will process them promptly and notify you when they’re ready for pickup or sent to your pharmacy.',
      path: '/patient-center/refill-requests'
    },
    officePolicies: {
      image: 'https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/Office+Policy.webp',
      infoTitle: 'Office Policies',
      infoText: 'Review our appointment, cancellation, and billing policies to ensure a smooth experience. Everything you need to know about visits, payments, and patient responsibilities.',
      path: '/patient-center/office-policies'
    },
    faqs: {
      image: 'https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/FAQ.webp',
      infoTitle: "FAQ's",
      infoText: 'Find quick answers to the most common questions—hours of operation, new patient procedures, after-hours care, and more—all in one place.',
      path: '/patient-center/faqs'
    }
  };
  
  

  const handleCardNavigation = (path) => {
    navigate(path);
  };

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (barRef.current) {
        const top = barRef.current.getBoundingClientRect().top;
        if (top <= 0) {
          barRef.current.classList.add('sticky', 'top-0', 'shadow-md');
        } else {
          barRef.current.classList.remove('sticky', 'top-0', 'shadow-md');
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Generate info cards for patient center
  const generateInfoCards = () => {
    const infoCardComponents = [];
    
    for (const key in infoCardsData) {
      const cardData = infoCardsData[key];
      infoCardComponents.push(
        <InfoCard
          key={key}
          image={cardData.image}
          infoTitle={cardData.infoTitle}
          infoText={cardData.infoText}
          path={cardData.path}
          onNavigate={handleCardNavigation}
        />
      );
    }
    
    return infoCardComponents;
  };

  return (
    <div>
      {/* Hero Section */}
      <div className="flex justify-center smartphone:hidden items-center relative">
        <div className="relative w-auto z-1 mt-[-140px]">
          <img
            className="w-full h-auto rounded-lg shadow-lg mt-2"
            src="https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/Patient+Center+(2).webp"
            alt="Patient Center"
          />
        </div>
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 smartphone:hidden">
          <button
            className="text-white font-bold bg-custom-regularOrange px-2 py-1 rounded-md hover:bg-custom-darkOrange"
            onClick={() => navigateAndScroll('/patient-center/new-patient')}
          >
            New to our practice - LET US HELP YOU
          </button>
        </div>
      </div>

      {/* Sticky Navigation Bar */}
      {/* <div ref={barRef} className="sticky top-0 z-20 flex justify-around bg-custom-blue text-white">
        <button className="p-1 text-sm cursor-pointer grow-text" onClick={() => scrollToSection('newPatient')}>New Patient Registration</button>
        <button className="p-1 text-sm cursor-pointer grow-text" onClick={() => scrollToSection('patientForms')}>Patient Forms</button>
        <button className="p-1 text-sm cursor-pointer grow-text" onClick={() => scrollToSection('refillRequests')}>Refill Requests</button>
        <button className="p-1 text-sm cursor-pointer grow-text" onClick={() => scrollToSection('officePolicies')}>Office Policies</button>
        <button className="p-1 text-sm cursor-pointer grow-text" onClick={() => scrollToSection('FAQs')}>FAQ's</button>
      </div> */}

      {/* <div ref={barRef} className="sticky top-0 flex justify-around bg-custom-blue text-white z-50">
        {Object.values(infoCardsData).map((card) => (
          <button
            key={card.infoTitle}
            className="p-1 text-sm cursor-pointer grow-text"
            onClick={() => navigateAndScroll(card.path, card.infoTitle)}
          >
            {card.infoTitle}
          </button>
        ))}
      </div> */}

      {/* Info Card Grid Section */}
      <div className="py-12 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8 text-gray-800">Patient Center</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mx-auto">
            {generateInfoCards()}
          </div>
        </div>
      </div>

      {/* Detailed Sections */}
      {/* <section id="newPatient" className="mb-4">
        <NewPatient />
      </section>
      <section id="patientForms">
        <PatientForms />
      </section>
      <section id="refillRequests">
        <RefillRequests />
      </section>
      <section id="officePolicies">
        <OfficePolicies />
      </section>
      <section id="FAQs">
        <FAQs />
      </section> */}
    </div>
  );
}

export default PatientCenter;
